import React, { useMemo, useState } from "react";
import { ArrowUpDown, ExternalLink } from "lucide-react";
import { Panel, CHIP } from "./ui";

const STATUS_LABEL = { active: "פעיל", inactive: "לא פעיל", blocked: "חסום" };
const STATUS_TONE = { active: "emerald", inactive: "slate", blocked: "rose" };

const COLUMNS = [
  { key: "name", label: "מורה" },
  { key: "privateLessons", label: "פרטיים" },
  { key: "groupLessons", label: "קבוצתיים" },
  { key: "teachingHours", label: "שעות" },
  { key: "students", label: "תלמידים" },
  { key: "revenue", label: "הכנסה" },
  { key: "approvalRate", label: "% אישור" },
  { key: "avgResponseMins", label: "זמן תגובה" },
  { key: "cancelRate", label: "% ביטולים" },
  { key: "rating", label: "דירוג" },
  { key: "status", label: "סטטוס" },
];

const Avatar = ({ name, picture }) => (
  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-indigo-100 flex items-center justify-center">
    {picture ? (
      <img src={picture} alt={name} className="w-full h-full object-cover" />
    ) : (
      <span className="text-xs font-bold text-indigo-700">{name?.charAt(0)}</span>
    )}
  </div>
);

const TopTeachersTable = ({ data, loading, error, onRetry, onViewProfile }) => {
  const [sortKey, setSortKey] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    if (!data) return [];
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") return sortDir === "desc" ? bv.localeCompare(av, "he") * -1 : av.localeCompare(bv, "he");
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <Panel
      eyebrow="מורים"
      title="המורים הפעילים ביותר"
      subtitle="מיון לפי כל עמודה"
      accent="indigo"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!loading && !error && (!data || data.length === 0)}
    >
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              {COLUMNS.map((c) => (
                <th key={c.key} className="text-right font-bold px-2 py-1.5 whitespace-nowrap">
                  <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-0.5 hover:text-gray-600">
                    {c.label} <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
              ))}
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <Avatar name={t.name} picture={t.picture} />
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 whitespace-nowrap">{t.name}</p>
                      <p className="text-[10px] text-gray-400 truncate max-w-[9rem]">{t.subjects.join(" · ")}</p>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-gray-600">{t.privateLessons}</td>
                <td className="px-2 py-2 text-gray-600">{t.groupLessons}</td>
                <td className="px-2 py-2 text-gray-600">{t.teachingHours}</td>
                <td className="px-2 py-2 text-gray-600">{t.students}</td>
                <td className="px-2 py-2 text-gray-600 whitespace-nowrap">₪{t.revenue.toLocaleString()}</td>
                <td className="px-2 py-2 text-gray-600">{t.approvalRate}%</td>
                <td className="px-2 py-2 text-gray-600 whitespace-nowrap">{t.avgResponseMins} דק׳</td>
                <td className="px-2 py-2 text-gray-600">{t.cancelRate}%</td>
                <td className="px-2 py-2 text-gray-600">{t.rating.toFixed(1)}</td>
                <td className="px-2 py-2">
                  <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${CHIP[STATUS_TONE[t.status]]}`}>
                    {STATUS_LABEL[t.status]}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => onViewProfile?.(t.id)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                  >
                    פרופיל <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export default TopTeachersTable;
